import toast from 'react-hot-toast';
import { addToSyncQueue } from './offlineStorage';

/**
 * Exponential backoff retry utility
 * Starts at 1s, doubles each retry, max 5 retries
 */
export const retryWithBackoff = async (
  fn,
  options = {}
) => {
  const {
    maxRetries = 5,
    baseDelay = 1000,
    onRetry = null,
    operationName = 'Operation',
    showToast = true
  } = options;

  let attempt = 0;
  let toastId = null;

  const cleanup = () => {
    if (toastId) {
      toast?.dismiss(toastId);
      toastId = null;
    }
  };

  while (attempt <= maxRetries) {
    // Check network before attempting
    if (!navigator.onLine) {
      cleanup();
      throw new NetworkOfflineError(`${operationName} failed: You are offline`);
    }

    try {
      const result = await fn();
      cleanup();
      return result;
    } catch (error) {
      // Don't retry on non-network errors (e.g., validation, auth)
      if (!isNetworkError(error) && attempt === 0) {
        cleanup();
        throw error;
      }

      if (attempt >= maxRetries) {
        cleanup();
        throw error;
      }

      attempt++;
      const delay = baseDelay * Math.pow(2, attempt - 1);
      const nextRetrySeconds = Math.round(delay / 1000);

      if (showToast) {
        cleanup();
        toastId = toast(
          (t) => RetryToast({
            t,
            operationName,
            attempt,
            maxRetries,
            nextRetrySeconds,
            onManualRetry: () => {
              toast?.dismiss(t?.id);
            }
          }),
          {
            duration: delay + 2000,
            position: 'top-right',
            style: {
              background: '#fffbeb',
              border: '1px solid #fde68a',
              padding: '0',
              borderRadius: '8px'
            }
          }
        );
      }

      onRetry?.(attempt, delay, error);

      await sleep(delay);
    }
  }
};

/**
 * Check if an error is a network/connectivity error
 */
export const isNetworkError = (error) => {
  if (!navigator.onLine) return true;
  if (!error) return false;

  const networkErrorMessages = [
    'network error',
    'fetch failed',
    'failed to fetch',
    'networkerror',
    'net::err',
    'connection refused',
    'connection reset',
    'timeout',
    'etimedout',
    'econnrefused',
    'enotfound',
    'socket hang up'
  ];

  const message = (error?.message || error?.toString() || '')?.toLowerCase();
  return networkErrorMessages?.some(msg => message?.includes(msg));
};

/**
 * Custom error class for offline state
 */
export class NetworkOfflineError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NetworkOfflineError';
    this.isOffline = true;
  }
}

/**
 * Sleep helper
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry toast component rendered inline
 */
const RetryToast = ({ t, operationName, attempt, maxRetries, nextRetrySeconds, onManualRetry }) => {
  return (
    <div className="flex items-start gap-3 p-4" role="alert" aria-live="assertive">
      <div className="flex-shrink-0 mt-0.5">
        <div className="w-5 h-5 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">
          {operationName} failed — retrying
        </p>
        <p className="text-xs text-amber-700 mt-0.5">
          Attempt {attempt}/{maxRetries} · Retrying in {nextRetrySeconds}s
        </p>
      </div>
      <button
        onClick={() => {
          onManualRetry?.();
          toast?.dismiss(t?.id);
        }}
        className="flex-shrink-0 px-2 py-1 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500"
        aria-label="Retry now"
      >
        Retry now
      </button>
    </div>
  );
};

/**
 * Wrap a Supabase operation with retry + offline queue fallback
 * If offline, queues the operation for later sync
 */
export const withRetryAndOfflineQueue = async (
  fn,
  queueOptions = {},
  retryOptions = {}
) => {
  const { table, operation, data } = queueOptions;

  // If offline, queue immediately
  if (!navigator.onLine) {
    if (table && operation && data) {
      await addToSyncQueue(table, operation, data);
      toast(
        <div role="alert" aria-live="assertive">
          <p className="text-sm font-semibold text-blue-800">Saved offline</p>
          <p className="text-xs text-blue-700 mt-0.5">Will sync when connection is restored</p>
        </div>,
        {
          duration: 4000,
          position: 'top-right',
          style: {
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            padding: '16px',
            borderRadius: '8px'
          },
          icon: '📶'
        }
      );
    }
    throw new NetworkOfflineError('You are offline. Operation queued for sync.');
  }

  return retryWithBackoff(fn, retryOptions);
};

export default retryWithBackoff;
